import { Image } from "@/components/inputs/MultiImageUpload";
import { toast } from "sonner";

type UploadImageResponse = {
    isDuplicate: boolean;
    uploadUrl?: string;
    image: Image;
};

async function calculateImageHash(imageFile: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (event) => {
            const arrayBuffer = event.target?.result as ArrayBuffer;
            try {
                const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                const imageHash = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
                resolve(imageHash);
            } catch (error) {
                reject(error);
            }
        };
        reader.onerror = (error) => {
            reject(error);
        };
        reader.readAsArrayBuffer(imageFile);
    });
}

async function _getUploadUrl(imageHash: string): Promise<UploadImageResponse | undefined> {
    try {
        const response = await fetch(
            `${import.meta.env.VITE_TRPC_SERVER_URL || "http://localhost:3000"}/upload/getUploadUrl`,
            {
                method: "POST",
                body: JSON.stringify({ imageHash }),
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
            }
        );
        if (!response.ok) {
            throw new Error("Failed to get upload URL");
        }
        return await response.json();
    } catch (error: any) {
        let errorMessage = "An error occurred while uploading the image";
        if (error.response?.status === 400) {
            errorMessage = "Invalid image format";
        }
        toast.error(errorMessage);
    }
}

export const uploadImage = async (file: File): Promise<{ webp: string; thumbnail?: string; error?: string }> => {
    const imageHash = await calculateImageHash(file);
    const uploadResponse = await _getUploadUrl(imageHash);

    if (!uploadResponse) {
        return { webp: "", thumbnail: "", error: "Failed to get upload URL" };
    }

    if (uploadResponse.isDuplicate) {
        return { webp: uploadResponse.image.webp, thumbnail: uploadResponse.image.thumbnail };
    }

    // If your API uses FormData, you can create it here.
    const formData = new FormData();
    formData.append("image", file);

    return await fetch(
        `${import.meta.env.VITE_TRPC_SERVER_URL || "http://localhost:3000"}${uploadResponse.uploadUrl}`,
        {
            method: "POST",
            body: formData,
            credentials: "include",
        }
    ).then((res) => res.json());
};

export const uploadImageWithRetry = async (
    file: File,
    retries = 3
): Promise<{ webp: string; thumbnail?: string; error?: string }> => {
    let response;
    for (let i = 0; i < retries; i++) {
        try {
            response = await uploadImage(file);
            if (!response.error) {
                return response;
            }
            else {
                toast.error(response.error);
                break;
            }
        } catch (error) {
            if (i === retries - 1) {
                console.error("Failed to upload image after multiple attempts", error);
                return { webp: "", thumbnail: "", error: "Failed to upload image after multiple attempts" };
            }
        }
        const backoffTime = Math.pow(2, i) * 1000;
        await new Promise((resolve) => setTimeout(resolve, backoffTime));
    }
    return response;
};

type UploadResponse = {
    isDuplicate: boolean;
    uploadUrl?: string;
    url: string;
};

async function calculateFileHash(fileFile: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (event) => {
            const arrayBuffer = event.target?.result as ArrayBuffer;
            try {
                const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                const fileHash = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
                resolve(fileHash);
            } catch (error) {
                reject(error);
            }
        };
        reader.onerror = (error) => {
            reject(error);
        };
        reader.readAsArrayBuffer(fileFile);
    });
}

async function getUploadUrl(fileHash: string, extension: string): Promise<UploadResponse | undefined> {
    try {
        const response = await fetch(
            `/api/v1/upload/getUploadUrl/${fileHash}/${extension}`,
            {
                method: "GET",
                credentials: "include",
            }
        );
        if (!response.ok) {
            throw new Error("Failed to get upload URL");
        }
        return await response.json();
    } catch (error: any) {
        let errorMessage = "An error occurred while uploading the file";
        if (error.response?.status === 400) {
            errorMessage = "Invalid file format";
        }
        toast.error(errorMessage);
    }
}

export const uploadFile = async (file: File): Promise<{ url: string, error?: string }> => {
    const fileHash = await calculateFileHash(file);
    const extension = file.name.split(".").pop();
    const uploadResponse = await getUploadUrl(fileHash, extension!);

    if (!uploadResponse) {
        return { url: "", error: "Failed to get upload URL" };
    }

    if (uploadResponse.isDuplicate) {
        return { url: uploadResponse.url };
    }

    // If your API uses FormData, you can create it here.
    const formData = new FormData();
    formData.append("file", file);

    return await fetch(
        `${uploadResponse.uploadUrl}`,
        {
            method: "POST",
            body: formData,
            credentials: "include",
        }
    ).then((res) => res.json());
};

export const uploadFileWithRetry = async (
    file: File,
    retries = 3
): Promise<{ url: string, error?: string }> => {
    let response: { url: string, error?: string } = { url: "" };
    for (let i = 0; i < retries; i++) {
        try {
            response = await uploadFile(file);
            if (!response.error) {
                return response;
            }
            else {
                toast.error(response.error);
                break;
            }
        } catch (error) {
            if (i === retries - 1) {
                console.error("Failed to upload file after multiple attempts", error);
                return { url: "", error: "Failed to upload file after multiple attempts" };
            }
        }
        const backoffTime = Math.pow(2, i) * 1000;
        await new Promise((resolve) => setTimeout(resolve, backoffTime));
    }
    return response;
};

// Branding-specific upload functions
async function getBrandingUploadUrl(fileHash: string, extension: string, type: 'logo' | 'favicon' | 'sidebar-logo'): Promise<UploadResponse | undefined> {
    const url = `/upload/branding/getUploadUrl/${type}/${fileHash}/${extension}`;
    console.log('🔗 Requesting upload URL:', url);

    try {
        const response = await fetch(url, {
            method: "GET",
            credentials: "include",
        });

        console.log('📡 Upload URL response status:', response.status);

        if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ Upload URL request failed:', errorText);
            throw new Error(`Failed to get branding upload URL: ${response.status} ${errorText}`);
        }

        const result = await response.json();
        console.log('✅ Upload URL response:', result);
        return result;
    } catch (error: any) {
        console.error('💥 getBrandingUploadUrl error:', error);
        let errorMessage = "An error occurred while uploading the branding asset";
        if (error.response?.status === 400) {
            errorMessage = "Invalid file format";
        }
        toast.error(errorMessage);
        return undefined;
    }
}

export const uploadBrandingAsset = async (file: File, type: 'logo' | 'favicon' | 'sidebar-logo'): Promise<{ url: string, error?: string }> => {
    console.log('🔄 Calculating file hash for:', file.name);
    const fileHash = await calculateFileHash(file);
    const extension = file.name.split(".").pop();

    console.log('📋 File details:', { hash: fileHash, extension, type });

    const uploadResponse = await getBrandingUploadUrl(fileHash, extension!, type);

    if (!uploadResponse) {
        console.error('❌ No upload response received');
        return { url: "", error: "Failed to get branding upload URL" };
    }

    if (uploadResponse.isDuplicate) {
        console.log('♻️ File already exists, returning existing URL:', uploadResponse.url);
        return { url: uploadResponse.url };
    }

    console.log('📤 Uploading file to:', uploadResponse.uploadUrl);

    if (!uploadResponse.uploadUrl) {
        console.error('❌ No upload URL provided');
        return { url: "", error: "No upload URL provided" };
    }

    const formData = new FormData();
    formData.append("file", file);

    const uploadUrl = uploadResponse.uploadUrl;
    console.log('🚀 Final upload URL:', uploadUrl);

    try {
        const response = await fetch(uploadUrl, {
            method: "POST",
            body: formData,
            credentials: "include",
        });

        console.log('📡 Upload response status:', response.status);

        if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ Upload failed:', errorText);
            return { url: "", error: `Upload failed: ${response.status} ${errorText}` };
        }

        const result = await response.json();
        console.log('✅ Upload successful:', result);
        return result;
    } catch (error) {
        console.error('💥 Upload exception:', error);
        return { url: "", error: `Upload exception: ${error}` };
    }
};

export const uploadBrandingAssetWithRetry = async (
    file: File,
    type: 'logo' | 'favicon' | 'sidebar-logo',
    retries = 3
): Promise<{ url: string, error?: string }> => {
    let response: { url: string, error?: string } = { url: "" };
    for (let i = 0; i < retries; i++) {
        try {
            response = await uploadBrandingAsset(file, type);
            if (!response.error) {
                return response;
            } else {
                toast.error(response.error);
                break;
            }
        } catch (error) {
            if (i === retries - 1) {
                console.error("Failed to upload branding asset after multiple attempts", error);
                return { url: "", error: "Failed to upload branding asset after multiple attempts" };
            }
        }
        const backoffTime = Math.pow(2, i) * 1000;
        await new Promise((resolve) => setTimeout(resolve, backoffTime));
    }
    return response;
};